import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Refresh, Plus, Trash2, Settings } from "@/components/icons";

import {
  fetchLoadBalancingGroups,
  createLoadBalancingGroup,
  updateLoadBalancingGroup,
  deleteLoadBalancingGroup,
  addUnitToGroup,
  removeUnitFromGroup,
  fetchGroupStatus,
  LBGroup,
} from "@/lib/api";
import { useAuth } from "@/app/AuthContext";
import { useDashboardOrg } from "@/app/DashboardOrgContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/Modal";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardTitle } from "@/components/ui/card";

function utilColor(pct: number): string {
  if (pct <= 70) return "progress-success";
  if (pct <= 90) return "progress-warning";
  return "progress-error";
}

export function LoadBalancingPage() {
  const { isFree } = useAuth();

  // Free-tier gate
  if (isFree) {
    return (
      <div className="space-y-5 pt-[86px]">
        <h1 className="text-2xl font-semibold">Load Balancing</h1>
        <Card>
          <CardContent className="items-center text-center">
            <div className="text-4xl">⚡</div>
            <div className="text-lg font-semibold">Prime Feature</div>
            <div className="opacity-70">
              Load Balancing is available on the Prime tier.
              Contact your administrator to upgrade.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <LoadBalancingContent />;
}

function LoadBalancingContent() {
  const qc = useQueryClient();
  // Manager → org-scoped LB (/api/org/<id>/load-balancing/...); admin → fleet. orgId
  // is in every query key so switching org refetches; scopeReady gates the manager
  // pre-selection window so no fleet request is issued.
  const { dashboardOrgId, scopeReady } = useDashboardOrg();
  // Deep-link target from the unit page's LB badge (?group=<id>): scroll to and
  // briefly highlight that group's card so an admin lands right on it.
  const [searchParams] = useSearchParams();
  const highlightGroupId = searchParams.get("group");
  const [createOpen, setCreateOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<LBGroup | null>(null);
  const [addUnitOpen, setAddUnitOpen] = useState<string | null>(null);
  const [newUnitId, setNewUnitId] = useState("");

  // Create form
  const [formId, setFormId] = useState("");
  const [formName, setFormName] = useState("");
  const [formMaxAmps, setFormMaxAmps] = useState("32");
  const [formMinAmps, setFormMinAmps] = useState("6");

  // §10 — on an org SWITCH (not initial mount), drop ALL per-org transient UI and drop
  // the PREVIOUS org's cached/in-flight LB queries so a dialog or mutation can never
  // target a group from the old org under the new org's URL. We scope the cancel to the
  // previous org id so the freshly-mounted new-org query is left to resolve. Group
  // lists/status are keyed by dashboardOrgId, so the new org's data (or an empty loading
  // state) replaces the old — no Org A groups are shown while Org B is selected.
  const prevOrgRef = useRef(dashboardOrgId);
  useEffect(() => {
    const prev = prevOrgRef.current;
    prevOrgRef.current = dashboardOrgId;
    if (prev === dashboardOrgId) return;
    setCreateOpen(false);
    setEditGroup(null);
    setAddUnitOpen(null);
    setNewUnitId("");
    setFormId(""); setFormName(""); setFormMaxAmps("32"); setFormMinAmps("6");
    qc.cancelQueries({ queryKey: ["load-balancing", prev] });
    qc.removeQueries({ queryKey: ["load-balancing", prev] });
  }, [dashboardOrgId, qc]);

  const groupsQ = useQuery({
    queryKey: ["load-balancing", dashboardOrgId, "groups"],
    queryFn: () => fetchLoadBalancingGroups(dashboardOrgId),
    enabled: scopeReady,
    refetchInterval: 5_000,
  });

  const createM = useMutation({
    mutationFn: () => createLoadBalancingGroup({
      group_id: formId,
      name: formName,
      max_amps: parseFloat(formMaxAmps) || 32,
      min_amps: parseFloat(formMinAmps) || 6,
    }, dashboardOrgId),
    onSuccess: () => {
      toast.success("Group created");
      setCreateOpen(false);
      setFormId(""); setFormName(""); setFormMaxAmps("32"); setFormMinAmps("6");
      qc.invalidateQueries({ queryKey: ["load-balancing"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed to create group"),
  });

  const updateM = useMutation({
    mutationFn: (patch: { groupId: string; name?: string; max_amps?: number; min_amps?: number }) =>
      updateLoadBalancingGroup(patch.groupId, { name: patch.name, max_amps: patch.max_amps, min_amps: patch.min_amps }, dashboardOrgId),
    onSuccess: () => {
      toast.success("Group updated");
      setEditGroup(null);
      qc.invalidateQueries({ queryKey: ["load-balancing"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed to update group"),
  });

  const deleteM = useMutation({
    mutationFn: (groupId: string) => deleteLoadBalancingGroup(groupId, dashboardOrgId),
    onSuccess: () => {
      toast.success("Group deleted");
      qc.invalidateQueries({ queryKey: ["load-balancing"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed to delete group"),
  });

  const addUnitM = useMutation({
    mutationFn: ({ groupId, unitId }: { groupId: string; unitId: string }) => addUnitToGroup(groupId, unitId, dashboardOrgId),
    onSuccess: () => {
      toast.success("Unit added");
      setAddUnitOpen(null);
      setNewUnitId("");
      qc.invalidateQueries({ queryKey: ["load-balancing"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed to add unit"),
  });

  const removeUnitM = useMutation({
    mutationFn: ({ groupId, unitId }: { groupId: string; unitId: string }) => removeUnitFromGroup(groupId, unitId, dashboardOrgId),
    onSuccess: () => {
      toast.success("Unit removed");
      qc.invalidateQueries({ queryKey: ["load-balancing"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed to remove unit"),
  });

  const groups: LBGroup[] = groupsQ.data?.groups ?? [];

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Load Balancing</h1>
          <div className="text-sm opacity-70">Circuit groups and current distribution</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => groupsQ.refetch()}>
            <Refresh className="h-4 w-4" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> New Group
          </Button>
        </div>
      </div>

      {groupsQ.isLoading ? (
        <div className="flex justify-center p-12">
          <span className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
        </div>
      ) : groupsQ.isError ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {(groupsQ.error as any)?.message?.includes("503")
            ? "Load balancing is not enabled on this server"
            : "Failed to load groups"}
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="items-center text-center">
            <div className="text-lg font-semibold">No Groups</div>
            <div className="opacity-70">Create a circuit group to get started.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {groups.map((g) => (
            <GroupCard
              key={g.group_id}
              group={g}
              highlight={g.group_id === highlightGroupId}
              onEdit={() => setEditGroup(g)}
              onDelete={() => { if (confirm(`Delete group "${g.name}"?`)) deleteM.mutate(g.group_id); }}
              onAddUnit={() => { setAddUnitOpen(g.group_id); setNewUnitId(""); }}
              onRemoveUnit={(unitId) => removeUnitM.mutate({ groupId: g.group_id, unitId })}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Create Circuit Group">
        <div className="space-y-3">
          <div>
            <Label>Group ID</Label>
            <Input className="mt-1" value={formId} onChange={(e) => setFormId(e.target.value)} placeholder="circuit-a" />
          </div>
          <div>
            <Label>Name</Label>
            <Input className="mt-1" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Circuit A" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Max Amps</Label>
              <Input className="mt-1" type="number" value={formMaxAmps} onChange={(e) => setFormMaxAmps(e.target.value)} />
            </div>
            <div>
              <Label>Min Amps (per unit)</Label>
              <Input className="mt-1" type="number" value={formMinAmps} onChange={(e) => setFormMinAmps(e.target.value)} />
            </div>
          </div>
          <Button className="w-full" onClick={() => createM.mutate()} disabled={createM.isPending || !formId || !formName}>
            {createM.isPending ? "Creating..." : "Create Group"}
          </Button>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editGroup} onClose={() => setEditGroup(null)} title={`Edit: ${editGroup?.name}`}>
        {editGroup && (
          <EditGroupForm
            group={editGroup}
            onSave={(patch) => updateM.mutate({ groupId: editGroup.group_id, ...patch })}
            isPending={updateM.isPending}
          />
        )}
      </Modal>

      {/* Add Unit Modal */}
      <Modal open={!!addUnitOpen} onClose={() => setAddUnitOpen(null)} title="Add Unit to Group">
        <div className="space-y-3">
          <div>
            <Label>Unit ID</Label>
            <Input className="mt-1" value={newUnitId} onChange={(e) => setNewUnitId(e.target.value)} placeholder="Enter unit ID..." />
          </div>
          <Button
            className="w-full"
            onClick={() => addUnitOpen && addUnitM.mutate({ groupId: addUnitOpen, unitId: newUnitId })}
            disabled={addUnitM.isPending || !newUnitId}
          >
            {addUnitM.isPending ? "Adding..." : "Add Unit"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function GroupCard({
  group,
  highlight = false,
  onEdit,
  onDelete,
  onAddUnit,
  onRemoveUnit,
}: {
  group: LBGroup;
  highlight?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onAddUnit: () => void;
  onRemoveUnit: (unitId: string) => void;
}) {
  const { dashboardOrgId, scopeReady } = useDashboardOrg();
  // Scroll the deep-linked card into view once (group id is stable), so a jump
  // from a unit's LB badge lands on the right card even below the fold.
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (highlight && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlight]);
  const statusQ = useQuery({
    queryKey: ["load-balancing", dashboardOrgId, "groups", group.group_id, "status"],
    queryFn: () => fetchGroupStatus(group.group_id, dashboardOrgId),
    enabled: scopeReady,
    refetchInterval: 5_000,
  });

  const status = statusQ.data;
  const utilPct = status?.utilization_pct ?? 0;
  const headroom = status?.headroom ?? group.max_amps;
  const totalDraw = status?.total_current_draw ?? 0;
  const phantomIds = status?.phantom_ids ?? [];
  const phantomCount = status?.phantom_count ?? 0;

  return (
    <div ref={cardRef} className="scroll-mt-24">
      <Card className={highlight ? "ring-2 ring-primary ring-offset-2 ring-offset-base-100" : undefined}>
      <CardContent>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <CardTitle>{group.name}</CardTitle>
              {phantomCount > 0 && (
                <Badge variant="error" className="text-[10px] h-4 px-1.5">
                  {phantomCount} phantom{phantomCount === 1 ? "" : "s"}
                </Badge>
              )}
            </div>
            <div className="text-xs font-mono opacity-60">{group.group_id}</div>
          </div>
          <div className="flex gap-1">
            <Button data-testid={`lb-edit-${group.group_id}`} variant="ghost" size="xs" onClick={onEdit}><Settings className="h-3 w-3" /></Button>
            <Button data-testid={`lb-delete-${group.group_id}`} variant="ghost" size="xs" className="text-error" onClick={onDelete}><Trash2 className="h-3 w-3" /></Button>
          </div>
        </div>

        {phantomCount > 0 && (
          <div className="mt-2 rounded border border-error/50 bg-error/10 p-2 text-xs">
            <div className="font-semibold text-error mb-1">
              Unknown unit IDs in this group — each reserves {group.min_amps}A of circuit capacity
            </div>
            {phantomIds.map((pid) => (
              <div key={pid} className="flex items-center justify-between py-0.5">
                <span className="font-mono opacity-80 break-all">{pid}</span>
                <Button variant="ghost" size="xs" className="text-error" onClick={() => onRemoveUnit(pid)}>Remove</Button>
              </div>
            ))}
          </div>
        )}

        {/* Utilization */}
        <div className="mt-3">
          <div className="flex justify-between text-xs mb-1">
            <span>Draw: {totalDraw.toFixed(1)}A / {group.max_amps}A</span>
            <span>Headroom: {headroom.toFixed(1)}A</span>
          </div>
          <progress
            className={`progress w-full ${utilColor(utilPct)}`}
            value={utilPct}
            max={100}
          />
          <div className="text-right text-xs opacity-60">{utilPct.toFixed(0)}% utilized</div>
        </div>

        {/* Units */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Units ({group.unit_ids?.length ?? 0})</span>
            <Button data-testid={`lb-addunit-${group.group_id}`} variant="ghost" size="xs" onClick={onAddUnit}>
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
          {status?.units?.length ? (
            <div className="space-y-1">
              {status.units.map((u) => {
                const showIdLine = !!u.name && u.name !== u.unit_id;
                return (
                  <div key={u.unit_id} className="flex items-center justify-between p-2 rounded bg-card/50 text-sm">
                    <Link
                      to={`/unit/${encodeURIComponent(u.unit_id)}`}
                      state={{ backTo: "/load-balancing", backLabel: "Load Balancing" }}
                      className="flex min-w-0 items-center gap-2 hover:underline"
                    >
                      <Badge variant={u.is_online ? "success" : "neutral"} className="text-[10px] h-4 px-1.5">
                        {u.is_online ? "ON" : "OFF"}
                      </Badge>
                      <div className="flex min-w-0 flex-col leading-tight">
                        <span className="truncate text-xs">{u.name || u.unit_id}</span>
                        {showIdLine && (
                          <span className="truncate font-mono text-[10px] opacity-60">{u.unit_id}</span>
                        )}
                      </div>
                    </Link>
                    <div className="flex items-center gap-2">
                      <span className="text-xs">{u.current_draw?.toFixed(1) ?? 0}A / {(u.allocated_amps ?? u.allocated ?? 0).toFixed(1)}A</span>
                      <Button data-testid={`lb-rm-${u.unit_id}`} variant="ghost" size="xs" className="text-error" onClick={() => onRemoveUnit(u.unit_id)}>×</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : group.unit_ids?.length ? (
            <div className="space-y-1">
              {group.unit_ids.map((uid) => (
                <div key={uid} className="flex items-center justify-between p-2 rounded bg-card/50 text-sm">
                  <Link
                    to={`/unit/${encodeURIComponent(uid)}`}
                    state={{ backTo: "/load-balancing", backLabel: "Load Balancing" }}
                    className="min-w-0 truncate font-mono text-xs hover:underline"
                  >
                    {uid}
                  </Link>
                  <Button data-testid={`lb-rm-${uid}`} variant="ghost" size="xs" className="text-error" onClick={() => onRemoveUnit(uid)}>×</Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs opacity-50">No units in this group</div>
          )}
        </div>

        {/* Last recalculation */}
        <div className="mt-2 text-xs opacity-50">
          Last recalculation: {status?.last_recalculation ? new Date(status.last_recalculation).toLocaleTimeString() : "Never"}
        </div>
      </CardContent>
      </Card>
    </div>
  );
}

function EditGroupForm({
  group,
  onSave,
  isPending,
}: {
  group: LBGroup;
  onSave: (patch: { name?: string; max_amps?: number; min_amps?: number }) => void;
  isPending: boolean;
}) {
  const [name, setName] = useState(group.name);
  const [maxAmps, setMaxAmps] = useState(String(group.max_amps));
  const [minAmps, setMinAmps] = useState(String(group.min_amps));

  return (
    <div className="space-y-3">
      <div>
        <Label>Name</Label>
        <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Max Amps</Label>
          <Input className="mt-1" type="number" value={maxAmps} onChange={(e) => setMaxAmps(e.target.value)} />
        </div>
        <div>
          <Label>Min Amps</Label>
          <Input className="mt-1" type="number" value={minAmps} onChange={(e) => setMinAmps(e.target.value)} />
        </div>
      </div>
      <Button
        className="w-full"
        onClick={() => onSave({ name, max_amps: parseFloat(maxAmps), min_amps: parseFloat(minAmps) })}
        disabled={isPending}
      >
        {isPending ? "Saving..." : "Save Changes"}
      </Button>
    </div>
  );
}
